import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import type { ListResult, MarketplaceListingView, ProductListItem } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { formatBRL, marketplaceIn } from "@cx/domain";
import { ContentHeader, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { PROVIDER_UI, listingRef, syncState } from "./labels";
import { useMarketplaceAccounts } from "./useAccounts";

/** Anúncios dos marketplaces (RF-VEN-09): vínculo com produtos, vínculo pelo SKU e sincronização. */
export function MarketplaceListingsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const search = useSearch({ from: "/vendas/marketplace/anuncios" });
  const navigate = useNavigate({ from: "/vendas/marketplace/anuncios" });
  const toast = useToast();
  const qc = useQueryClient();
  const accounts = useMarketplaceAccounts();
  const list = accounts.data?.accounts ?? [];
  const account =
    list.find((a) => a.id === search.conta) ?? list.find((a) => a.status !== "disconnected") ?? list[0];
  const canUpdate = can(perms, "marketplaces", "update") && account?.status === "active";
  const ui = PROVIDER_UI[account?.provider ?? "mercadolivre"];
  const [q, setQ] = useState("");
  const [linked, setLinked] = useState<"" | "yes" | "no">("");
  const [linking, setLinking] = useState<MarketplaceListingView | null>(null);
  const key = ["marketplace-listings", companyId, account?.id, q, linked];
  const listings = useInfiniteQuery({
    queryKey: key,
    enabled: !!account,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api<ListResult<MarketplaceListingView>>(
        `/marketplaces/accounts/${account!.id}/listings?${new URLSearchParams({
          ...(q ? { q } : {}),
          ...(linked ? { linked } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (p) => p.nextCursor,
  });
  const rows = listings.data?.pages.flatMap((p) => p.data) ?? [];
  const total = listings.data?.pages[0]?.summary.count;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["marketplace-listings", companyId] });
    void qc.invalidateQueries({ queryKey: ["marketplace-accounts", companyId] });
  };
  const bySku = useMutation({
    mutationFn: () =>
      api<{ linked: number }>(`/marketplaces/accounts/${account!.id}/listings/link-by-sku`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
      }),
    onSuccess: (r) => {
      toast(
        "success",
        r.linked ? `${r.linked} anúncio(s) vinculado(s) pelo SKU` : "Nenhum SKU igual encontrado",
      );
      refresh();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const sync = useMutation({
    mutationFn: () =>
      api<{ pending: number }>(`/marketplaces/accounts/${account!.id}/sync`, { method: "POST" }),
    onSuccess: (r) => {
      toast("success", `Sincronização agendada (${r.pending} pendente(s))`);
      refresh();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const importAll = useMutation({
    mutationFn: () =>
      api<unknown>(`/marketplaces/accounts/${account!.id}/listings/refresh`, { method: "POST" }),
    onSuccess: () => toast("success", "Importação dos anúncios iniciada. Atualize em instantes."),
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <>
      <ContentHeader
        title="Anúncios do marketplace"
        breadcrumb={["Vendas", "Anúncios do marketplace"]}
        actions={
          canUpdate ? (
            <div className="d-flex flex-wrap gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => importAll.mutate()}>
                Importar anúncios
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => bySku.mutate()}>
                Vincular pelo SKU
              </button>
              {(account?.syncStock || account?.syncPrice) && (
                <button type="button" className="btn btn-primary" onClick={() => sync.mutate()}>
                  Sincronizar agora
                </button>
              )}
            </div>
          ) : undefined
        }
      />
      <QueryStatus loading={accounts.isLoading} error={accounts.error} />
      {accounts.isSuccess && list.length === 0 && (
        <div className="box card p-3 text-muted">
          Nenhuma conta conectada. Conecte em Preferências › Integrações.
        </div>
      )}
      {account && (
        <div className="box card">
          <div className="row g-2 p-3 pb-0 align-items-end">
            <Field label="Conta" className="col-md-4">
              <select
                className="form-select"
                value={account.id}
                onChange={(e) => navigate({ search: { conta: e.target.value }, replace: true })}
              >
                {list.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.storeName} ({PROVIDER_UI[a.provider].label})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Buscar" className="col-md-4">
              <input
                type="search"
                value={q}
                maxLength={100}
                placeholder={`Título, SKU ou código ${ui.itemCode}`}
                onChange={(e) => setQ(e.target.value)}
              />
            </Field>
            <Field label="Vínculo" className="col-md-4">
              <select
                className="form-select"
                value={linked}
                onChange={(e) => setLinked(e.target.value as "" | "yes" | "no")}
              >
                <option value="">Todos</option>
                <option value="no">Sem vínculo</option>
                <option value="yes">Vinculados</option>
              </select>
            </Field>
          </div>
          {total !== undefined && total >= 0 && <p className="small px-3 mb-0">{total} anúncio(s)</p>}
          <QueryStatus loading={listings.isLoading} error={listings.error} />
          {listings.isSuccess && rows.length === 0 && (
            <p className="text-muted p-3 mb-0">
              Nenhum anúncio. Use “Importar anúncios” para buscar {marketplaceIn(account.provider)}.
            </p>
          )}
          {rows.length > 0 && (
            <div className="table-responsive mt-2">
              <table className="table align-middle mb-0" aria-label="Anúncios">
                <thead>
                  <tr>
                    <th scope="col">Anúncio</th>
                    <th scope="col">SKU</th>
                    <th scope="col" className="text-end">
                      Preço {ui.shortIn}
                    </th>
                    <th scope="col" className="text-end">
                      Estoque {ui.shortIn}
                    </th>
                    <th scope="col">Produto</th>
                    <th scope="col">Sincronização</th>
                    <th scope="col">
                      <span className="visually-hidden">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((l) => {
                    const st = syncState(l);
                    return (
                      <tr key={l.id}>
                        <td>
                          <div>
                            {l.title}
                            {l.variationLabel ? ` · ${l.variationLabel}` : ""}
                          </div>
                          <div className="small">
                            {listingRef(l)}
                            {l.fulfillment ? " · Full (estoque do ML)" : ""}
                          </div>
                        </td>
                        <td>{l.sku ?? "—"}</td>
                        <td className="text-end">{l.priceCents === null ? "—" : formatBRL(l.priceCents)}</td>
                        <td className="text-end">{l.availableQty ?? "—"}</td>
                        <td>{l.product ? l.product.name : "—"}</td>
                        <td>
                          <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                          {l.syncError && l.syncError !== "price_conflict" && (
                            <div className="small text-danger">{l.syncError}</div>
                          )}
                        </td>
                        <td className="text-end">
                          {canUpdate && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setLinking(l)}
                            >
                              {l.product ? "Trocar vínculo" : "Vincular"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {listings.hasNextPage && (
            <div className="p-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={listings.isFetchingNextPage}
                onClick={() => void listings.fetchNextPage()}
              >
                Carregar mais
              </button>
            </div>
          )}
        </div>
      )}
      {linking && (
        <LinkDialog
          listing={linking}
          onClose={() => setLinking(null)}
          onDone={() => {
            setLinking(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

/** Escolhe o produto do anúncio (busca por nome/SKU; variações aparecem, pais de variação não). */
function LinkDialog({
  listing,
  onClose,
  onDone,
}: {
  listing: MarketplaceListingView;
  onClose(): void;
  onDone(): void;
}) {
  const { companyId, api } = useSession();
  const toast = useToast();
  const [q, setQ] = useState(listing.sku ?? "");
  const products = useQuery({
    queryKey: ["products", companyId, "ml-link", q],
    queryFn: () =>
      api<ListResult<ProductListItem>>(
        `/products?${new URLSearchParams({ q, status: "active", includeVariations: "yes", limit: "20" })}`,
      ),
  });
  const link = useMutation({
    mutationFn: (productId: string | null) =>
      api<void>(`/marketplaces/listings/${listing.id}/link`, {
        method: "PUT",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ productId }),
      }),
    onSuccess: (_r, productId) => {
      toast("success", productId ? "Anúncio vinculado" : "Vínculo removido");
      onDone();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const options = (products.data?.data ?? []).filter((p) => p.format !== "V");
  return (
    <Modal
      open
      title={`Vincular: ${listing.title}${listing.variationLabel ? ` · ${listing.variationLabel}` : ""}`}
      onClose={onClose}
      footer={
        listing.product ? (
          <button type="button" className="btn btn-outline-danger" onClick={() => link.mutate(null)}>
            Remover vínculo
          </button>
        ) : undefined
      }
    >
      <Field label="Produto (nome ou SKU)">
        <input
          type="search"
          data-autofocus
          value={q}
          maxLength={100}
          onChange={(e) => setQ(e.target.value)}
        />
      </Field>
      <QueryStatus loading={products.isLoading} error={products.error} />
      {products.isSuccess && options.length === 0 && (
        <p className="text-muted">Nenhum produto ativo encontrado.</p>
      )}
      <ul className="list-group" aria-label="Produtos">
        {options.map((p) => (
          <li key={p.id} className="list-group-item d-flex align-items-center gap-2">
            <span className="flex-grow-1">
              {p.name}
              {p.sku ? <span className="small"> · {p.sku}</span> : null}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={link.isPending}
              aria-label={`Vincular a ${p.name}`}
              onClick={() => link.mutate(p.id)}
            >
              Vincular
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
